import React, { useCallback, useEffect, useMemo } from 'react'
import { DashboardInvestmentTransactions } from '../../../lib/types/propsType_v1'
import {
  formatTransactionAmountDisplay,
  formatTransactionQuantityDisplay,
  formatTransactionType,
  isSellTransaction,
} from '../dashboard-v2.utils'

type TransactionDetailsModalProps = {
  isOpen: boolean
  transaction: DashboardInvestmentTransactions | null
  onClose: () => void
}

type DetailItemProps = {
  label: string
  value: string
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
  <div className='dashboard-v2__withdrawal-form__detail-item'>
    <span className='dashboard-v2__withdrawal-form__detail-label'>{label}</span>
    <span className='dashboard-v2__withdrawal-form__detail-value'>{value}</span>
  </div>
)

const formatDate = (iso?: string) => {
  if (!iso) return '—'
  const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const openDocumentDownload = (downloadLink: string) => {
  window.open(downloadLink, '_blank', 'noopener,noreferrer')
}

const formatOptionalCents = (value: number | null | undefined): string | null => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null
  return formatTransactionAmountDisplay(Number(value), 'BUY')
}

export const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  transaction,
  onClose,
}) => {
  const resetAndClose = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resetAndClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, resetAndClose])

  const amountDisplay = useMemo(() => {
    if (!transaction) return '—'
    return formatTransactionAmountDisplay(transaction.amount, transaction.transaction_type)
  }, [transaction])

  const typeDisplay = useMemo(() => {
    if (!transaction) return '—'
    return formatTransactionType(transaction.transaction_type)
  }, [transaction])

  const unitsDisplay = useMemo(() => {
    if (!transaction || transaction.quantity === null || transaction.quantity === undefined) {
      return null
    }
    return formatTransactionQuantityDisplay(transaction.quantity)
  }, [transaction])

  const isSell = isSellTransaction(transaction?.transaction_type)

  const actualAmountReceivedDisplay = useMemo(
    () => formatOptionalCents(transaction?.actual_amount_received),
    [transaction],
  )

  const actualInvestedAmountDisplay = useMemo(() => {
    if (isSell) return null
    return formatOptionalCents(transaction?.actual_invested_amount)
  }, [transaction, isSell])

  const actualAmountSentDisplay = useMemo(() => {
    if (!isSell) return null
    return formatOptionalCents(transaction?.actual_amount_sent)
  }, [transaction, isSell])

  const termsheetLink = transaction?.termsheet?.downloadLink || null

  const allotmentLink = transaction?.allotmentConfirmation?.downloadLink || null

  const paymentCopyLink = transaction?.paymentCopy?.downloadLink || null

  if (!isOpen || !transaction) return null

  return (
    <div
      className='dashboard-v2__withdrawal-modal-backdrop'
      role='presentation'
      onClick={(e) => {
        if (e.target === e.currentTarget) resetAndClose()
      }}
    >
      <div
        className='dashboard-v2__withdrawal-form'
        role='dialog'
        aria-modal='true'
        aria-labelledby='transaction-details-title'
        onClick={(e) => e.stopPropagation()}
        data-testid='transaction-details-modal'
      >
        <header className='dashboard-v2__withdrawal-form__header'>
          <div className='dashboard-v2__withdrawal-form__header-text'>
            <p className='dashboard-v2__withdrawal-form__eyebrow'>Transaction</p>
            <h2 id='transaction-details-title'>Transaction details</h2>
            <div className='dashboard-v2__withdrawal-form__asset-chip'>
              <span className='dashboard-v2__withdrawal-form__asset-chip-title'>
                {transaction.opportunity_title || 'Transaction'}
              </span>
              {transaction.asset_class ? (
                <span className='dashboard-v2__withdrawal-form__asset-chip-badge'>
                  {transaction.asset_class}
                </span>
              ) : null}
            </div>
            <p className='dashboard-v2__withdrawal-form__subtitle'>
              {typeDisplay} · {amountDisplay} · Completed
            </p>
          </div>
          <div
            role='button'
            tabIndex={0}
            aria-label='Close dialog'
            className='dashboard-v2__holdings-actions-panel__close'
            onClick={resetAndClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                resetAndClose()
              }
            }}
          >
            ×
          </div>
        </header>

        <div className='dashboard-v2__withdrawal-form__form'>
          <section className='dashboard-v2__withdrawal-form__section'>
            <h3 className='dashboard-v2__withdrawal-form__section-title'>
              Transaction information
            </h3>
            <div className='dashboard-v2__withdrawal-form__details-grid'>
              <DetailItem label='Opportunity' value={transaction.opportunity_title || '—'} />
              {transaction.asset_class ? (
                <DetailItem label='Asset class' value={transaction.asset_class} />
              ) : null}
              <DetailItem label='Date' value={formatDate(transaction.transaction_date)} />
              <DetailItem label='Type' value={typeDisplay} />
              <DetailItem label='Amount' value={amountDisplay} />
              {actualAmountReceivedDisplay !== null ? (
                <DetailItem label='Actual amount received' value={actualAmountReceivedDisplay} />
              ) : null}
              {actualInvestedAmountDisplay !== null ? (
                <DetailItem label='Actual invested amount' value={actualInvestedAmountDisplay} />
              ) : null}
              {actualAmountSentDisplay !== null ? (
                <DetailItem label='Actual amount sent' value={actualAmountSentDisplay} />
              ) : null}
              {unitsDisplay !== null ? (
                <DetailItem label={transaction.shares ? 'Shares' : 'Units'} value={unitsDisplay} />
              ) : null}
              {transaction.shares && transaction.price_per_share ? (
                <DetailItem
                  label='Price per share'
                  value={formatOptionalCents(transaction.price_per_share) ?? '—'}
                />
              ) : null}
              <DetailItem label='Status' value='Completed' />
              <DetailItem
                label='Order ID'
                value={
                  transaction.order_id && transaction.order_id !== '-' ? transaction.order_id : '—'
                }
              />
            </div>
          </section>
        </div>

        <div className='dashboard-v2__withdrawal-form__footer'>
          <p className='dashboard-v2__withdrawal-form__footer-note'>
            {isSellTransaction(transaction.transaction_type)
              ? 'This sale has been completed and reflected in your portfolio.'
              : 'This purchase has been completed and reflected in your portfolio.'}
          </p>
          <div className='dashboard-v2__withdrawal-form__footer-actions'>
            {termsheetLink ? (
              <button
                type='button'
                className='secondary'
                onClick={() => openDocumentDownload(termsheetLink)}
                data-testid='transaction-details-termsheet-download'
              >
                Download termsheet
              </button>
            ) : null}
            {paymentCopyLink ? (
              <button
                type='button'
                className='secondary'
                onClick={() => openDocumentDownload(paymentCopyLink)}
                data-testid='transaction-details-payment-copy-download'
              >
                Download payment copy
              </button>
            ) : null}
            {allotmentLink ? (
              <button
                type='button'
                className='secondary'
                onClick={() => openDocumentDownload(allotmentLink)}
                data-testid='transaction-details-allotment-download'
              >
                Download allotment
              </button>
            ) : null}
            <button type='button' className='secondary' onClick={resetAndClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
